import { NavigateFunction } from 'react-router-dom';
import React from 'react';
import { BookOpen, Search, Book, FileText, MessageCircle, Shield, Save, Share2, Gavel } from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/providers/AuthProvider";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

// Import all renderers
import { renderPrayerContent } from "../components/detailRenderers/PrayerRenderer";
import { renderFindYourVerseContent } from "../components/detailRenderers/FindYourVerseRenderer";
import { renderDeepDiveContent } from "../components/detailRenderers/DeepDiveRenderer";
import { renderReadingPlanContent } from "../components/detailRenderers/ReadingPlanRenderer";
import { renderScriptureNavigatorContent } from "../components/detailRenderers/ScriptureNavigatorRenderer";
import { renderEtymologyContent } from "../components/detailRenderers/EtymologyRenderer";
import { renderAtheismDefenseContent } from "../components/detailRenderers/AtheismDefenseRenderer";
import { renderBiblicalDefenseContent } from "../components/detailRenderers/BiblicalDefenseRenderer";
import { renderInterfaithDialogueContent } from "../components/detailRenderers/InterfaithDialogue";
import { renderMoralDilemmasContent } from "../components/detailRenderers/MoralDilemmasRenderer";

// Tool name utility
export const getToolName = (toolType: string): string => {
  switch (toolType) {
    case 'atheism_defense':
      return 'Atheism Defense';
    case 'biblical_defense':
      return 'Biblical Defense';
    case 'interfaith_dialogue':
      return 'Interfaith Dialogue';
    case 'deep_dive':
      return 'Deep Dive';
    case 'etymology':
      return 'Etymology';
    case 'find_your_verse':
      return 'Find Your Verse';
    case 'prayer':
      return 'Prayer';
    case 'reading_plan':
      return 'Reading Plan';
    case 'scripture_navigator':
      return 'Scripture Navigator';
    case 'moral_dilemmas':
      return 'Moral & Ethical Dilemmas';
    default:
      return 'Bible Tool';
  }
};

// Tool icon utility
export const getToolIcon = (toolType: string): React.ReactNode => {
  switch (toolType) {
    case 'atheism_defense':
      return <Shield className="h-4 w-4 text-heaven-gold" />;
    case 'biblical_defense':
      return <Shield className="h-4 w-4 text-heaven-gold" />;
    case 'interfaith_dialogue':
      return <MessageCircle className="h-4 w-4 text-heaven-gold" />;
    case 'deep_dive':
      return <Book className="h-4 w-4 text-heaven-gold" />;
    case 'etymology':
      return <FileText className="h-4 w-4 text-heaven-gold" />;
    case 'find_your_verse':
      return <Search className="h-4 w-4 text-heaven-gold" />;
    case 'prayer':
      return <FileText className="h-4 w-4 text-heaven-gold" />;
    case 'reading_plan':
      return <BookOpen className="h-4 w-4 text-heaven-gold" />;
    case 'scripture_navigator':
      return <BookOpen className="h-4 w-4 text-heaven-gold" />;
    case 'moral_dilemmas':
      return <Gavel className="h-4 w-4 text-heaven-gold" />;
    default:
      return <FileText className="h-4 w-4 text-heaven-gold" />;
  }
};

// Content preview utility
export const renderContentPreview = (item: any): React.ReactNode => {
  if (!item || !item.content) return "No content available";
  
  const content = item.content;
  
  switch (item.tool_type) {
    case 'atheism_defense':
      return content.response?.directAnswer?.substring(0, 100) + '...' || "No preview available";
    case 'biblical_defense':
      return content.response?.directAnswer?.substring(0, 100) + '...' || "No preview available";
    case 'interfaith_dialogue':
      return content.response?.directAnswer?.substring(0, 100) + '...' || "No preview available";
    case 'deep_dive':
      return content.response?.analysis?.substring(0, 100) + '...' || "No preview available";
    case 'etymology':
      return content.response?.wordMeaning?.substring(0, 100) + '...' || "No preview available";
    case 'find_your_verse':
      return content.response?.verses?.[0]?.text?.substring(0, 100) + '...' || "No preview available";
    case 'prayer':
      return content.response?.prayer?.substring(0, 100) + '...' || "No preview available";
    case 'reading_plan':
      return content.response?.plan?.title || "No preview available";
    case 'scripture_navigator':
      return content.response?.summary?.substring(0, 100) + '...' || "No preview available";
    case 'moral_dilemmas':
      return content.response?.directAnswer?.substring(0, 100) + '...' || "No preview available";
    default:
      return "No preview available";
  }
};

// Response detail renderer
export const renderResponseDetail = (item: any, navigate: NavigateFunction) => {
  if (!item) return null;

  switch (item.tool_type) {
    case 'prayer':
      return renderPrayerContent(item, navigate);
    case 'find_your_verse':
      return renderFindYourVerseContent(item, navigate);
    case 'deep_dive':
      return renderDeepDiveContent(item, navigate);
    case 'reading_plan':
      return renderReadingPlanContent(item, navigate);
    case 'scripture_navigator':
      return renderScriptureNavigatorContent(item, navigate);
    case 'etymology':
      return renderEtymologyContent(item, navigate);
    case 'atheism_defense':
      return renderAtheismDefenseContent(item, navigate);
    case 'biblical_defense':
      return renderBiblicalDefenseContent(item, navigate);
    case 'interfaith_dialogue':
      return renderInterfaithDialogueContent(item, navigate);
    case 'moral_dilemmas':
      return renderMoralDilemmasContent(item, navigate);
    default:
      return <p>No renderer available for this response type.</p>;
  }
};

// Response actions component
interface ResponseActionsProps {
  question: string;
  response: any;
  toolType: string;
}

export const ResponseActions: React.FC<ResponseActionsProps> = ({ question, response, toolType }) => {
  const { user } = useAuth();

  const saveResponse = async () => {
    if (!response || !user) {
      toast({
        title: "Cannot save response",
        description: user ? "No response to save" : "Please sign in to save responses",
        variant: "destructive"
      });
      return;
    }

    try {
      // Create a JSON-compatible version of the response
      const contentObj = {
        question,
        response,
        createdAt: new Date().toISOString()
      };
      
      const { error: insertError, data: savedData } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: toolType,
          title: `${toolType}: ${question.substring(0, 50)}${question.length > 50 ? '...' : ''}`,
          content: contentObj,
          user_id: user.id
        })
        .select();

      if (insertError) {
        console.error("Error inserting saved response:", insertError);
        throw insertError;
      }

      console.log("Response saved successfully:", savedData);

      toast({
        title: "Response Saved",
        description: "Your response has been saved to your dashboard."
      });
    } catch (error) {
      console.error('Error saving response:', error);
      toast({
        title: "Error",
        description: "Failed to save response. Please try again later.",
        variant: "destructive"
      });
    }
  };

  const shareViaEmail = () => {
    if (!response) return;
    
    const subject = encodeURIComponent(`${toolType}: ${question}`);
    const body = encodeURIComponent(`
I wanted to share this response with you:

Question: ${question}

Response:
${JSON.stringify(response, null, 2)}

~ Shared from Selah App
    `);
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    
    toast({
      title: "Email Client Opened",
      description: "Your email client should be opening to share this content.",
    });
  };

  const shareViaSMS = () => {
    if (!response) return;
    
    const text = encodeURIComponent(`
${toolType}: ${question}

Response:
${JSON.stringify(response, null, 2)}

~ Shared from Selah App
    `);
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.location.href = `sms:?&body=${text}`;
    } else {
      navigator.clipboard.writeText(text).then(() => {
        toast({
          title: "Copied to Clipboard",
          description: "The content has been copied to your clipboard as SMS sharing is only available on mobile devices.",
        });
      });
    }
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    window.open(facebookShareUrl, '_blank', 'width=600,height=400');
    
    toast({
      title: "Sharing to Facebook",
      description: "A Facebook share dialog should have opened.",
    });
  };

  return (
    <div className="flex space-x-4 mt-6 mb-2 justify-center">
      {user && (
        <Button
          onClick={saveResponse}
          variant="outline"
          className="flex items-center gap-2 border-heaven-gold hover:bg-heaven-gold/10"
        >
          <Save className="h-5 w-5 text-heaven-gold" />
          <span>Save Response</span>
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="flex items-center gap-2 border-heaven-gold hover:bg-heaven-gold/10"
          >
            <Share2 className="h-5 w-5 text-heaven-gold" />
            <span>Share</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="bg-white border border-gray-100 shadow-lg"
        >
          <DropdownMenuLabel>Share Response</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={shareViaEmail} className="cursor-pointer">
            Email
          </DropdownMenuItem>
          <DropdownMenuItem onClick={shareViaSMS} className="cursor-pointer">
            Text Message
          </DropdownMenuItem>
          <DropdownMenuItem onClick={shareToFacebook} className="cursor-pointer">
            Facebook
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
